/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import * as d3 from 'd3';
import cloud from 'd3-cloud';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { cloneDeep, debounce } from 'lodash';
import stringResource from 'ba-common-nls';

import './WordCloud.scss';

const DEFAULT_OPACITY = 0.4;
const HOVER_OPACITY = 0.7;
const SELECTED_OPACITY = 0.9;

export type Words = {
	word: string;
	idx: number;
}[];

export default class WordCloud extends Component<any, any> {
	static propTypes = {
		words: PropTypes.array.isRequired,
		colorRange: PropTypes.array.isRequired,
		fontFamily: PropTypes.array.isRequired,
		selectedWords: PropTypes.array,
		onWordClick: PropTypes.func,
		width: PropTypes.number,
		height: PropTypes.number,
		padding: PropTypes.number,
	};

	static defaultProps = {
		width: '100%',
		height: '100%',
		selectedWords: [],
		padding: 2,
	};

	constructor(props) {
		super(props);
		this._debouncedResize = debounce(this._resize, 1000);
		this._currentFocusIdx = 0;
		this._clickIgnore = false;
		this._clickTimerID = 0;
	}

	componentDidMount() {
		window.addEventListener('resize', this._debouncedResize);
		this._init();
	}

	componentDidUpdate() {
		this._withNewWords ? this._start() : this._update();
	}

	componentWillUnmount() {
		this._el = null;
		window.removeEventListener('resize', this._debouncedResize);
	}

	getSnapshotBeforeUpdate(prevProps) {
		this._withNewWords = prevProps.words.length !== this.props.words.length;
	}

	_debouncedResize: any;
	_currentFocusIdx: number;
	_clickIgnore: boolean;
	_clickTimerID: number;
	_withNewWords: boolean;
	_el: HTMLElement;
	_canvas: any;
	_cloud: any;
	_words: Words;
	_tailEl: HTMLElement;

	_init = () => {
		const width = this._el.offsetWidth;
		const height = this._el.offsetHeight;

		this._canvas = d3
			.select(this._el)
			.append('svg')
			.attr('width', '100%')
			.attr('height', '100%')
			.append('g')
			.attr('transform', `translate(${width / 2},${height / 2})`);

		this._cloud = cloud()
			.size([width, height])
			.padding(this.props.padding)
			.rotate(0)
			.font(this.props.fontFamily)
			.fontSize(d => d.size)
			.on('end', this._update);

		this._start();
	};

	_resize = () => {
		this._cloud.stop();
		while (this._el && this._el.firstChild) {
			this._el.removeChild(this._el.firstChild);
		}
		this._init();
	};

	_fontWeight = d => (d.selected ? 'bold' : 'normal');

	_start = () => {
		// need a copy of words, since it will be mutated by D3 wordcloud after.
		this._words = cloneDeep(this.props.words);
		this._cloud.words(this._words).start();
	};

	_fill = d => {
		const { colorRange } = this.props;

		return d3
			.scaleQuantize()
			.domain([0, colorRange.length - 1])
			.range(colorRange)(d.sourceIdx % colorRange.length);
	};

	_opacity = d =>
		d.selected || !this.props.selectedWords.length ? SELECTED_OPACITY : DEFAULT_OPACITY;

	_getNextFocus = (isBackward?: boolean) => {
		const { length } = this.props.words;
		let nextFocusIdx = this._currentFocusIdx;

		if (isBackward) {
			nextFocusIdx -= 1;
			if (nextFocusIdx < 0) {
				nextFocusIdx = length - 1;
			}
		} else {
			nextFocusIdx += 1;
			if (nextFocusIdx >= length) {
				nextFocusIdx = 0;
			}
		}

		return nextFocusIdx;
	};

	_keydown = d => {
		// right arrow
		if (d3.event.keyCode === 39) {
			this._setFocus(this._getNextFocus());
		}

		// left arrow
		if (d3.event.keyCode === 37) {
			this._setFocus(this._getNextFocus(true));
		}

		// tab
		if (d3.event.keyCode === 9) {
			this._tailEl.focus();
		}

		// shift tab
		if (d3.event.shiftKey && d3.event.keyCode === 9) {
			this._el.focus();
		}

		// space
		if (d3.event.keyCode === 32) {
			this.props.onWordClick(d);
		}
	};

	_setFocus = (idx?: number) => {
		if (idx != null) {
			this._currentFocusIdx = idx;
		}

		const nodes = this._canvas.selectAll('g').nodes();
		const currentNode = nodes[this._currentFocusIdx];

		currentNode && currentNode.focus && currentNode.focus();
	};

	_click = (d, idx) => {
		// create mechanism to ignore 'click' event if we also received 'dblclick'
		this._clickTimerID = window.setTimeout(() => {
			if (!this._clickIgnore) {
				this._setFocus(idx);
				this.props.onWordClick(d);
			}
			this._clickIgnore = false;
		}, 300 /* arbitrary */);
	};

	_dblclick = (d, idx) => {
		// ignore 'click' event
		clearTimeout(this._clickTimerID);
		this._clickIgnore = true;
		// mimic 'click' event
		this._setFocus(idx);
		this.props.onWordClick(d);
	};

	_update = () => {
		this._words = this._words.map((word, idx) => ({ ...word, ...this.props.words[idx] }));

		const allWords = this._canvas.selectAll('g').data(this._words, d => {
			d.selected = this.props.selectedWords.indexOf(d.text) > -1;

			return [d.text, d.size];
		});
		const group = allWords.enter().append('g');
		const self = this;

		group.append('title').text(d => d.title);
		group.append('text');
		group
			.merge(allWords)
			.select('text')
			.style('fill', this._fill)
			.style('font-family', this.props.fontFamily)
			.style('font-weight', this._fontWeight)
			.style('font-size', d => `${d.size}px`)
			.attr('text-anchor', 'middle')
			.attr('transform', d => `translate(${d.x}, ${d.y})`)
			.style('cursor', 'pointer')
			.style('opacity', this._opacity)
			.text(d => d.text)
			.on('mouseover', function(d) {
				if (!d.selected) {
					d3.select(this)
						.transition()
						.style('opacity', HOVER_OPACITY);
				}
			})
			.on('mouseout', function(d) {
				if (!d.selected) {
					d3.select(this)
						.transition()
						.style('opacity', self._opacity); // eslint-disable-line
				}
			});
		group
			.attr('tabindex', 0)
			.on('keydown', this._keydown)
			.on('click', this._click)
			.on('dblclick', this._dblclick)
			// To make sure the correct keywords gets the focus when the init tabbing launched on the word cloud
			.on('focus', () => this._setFocus());

		allWords.exit().remove();
	};

	render() {
		const { width, height } = this.props;

		return (
			<>
				<div
					tabIndex={0}
					ref={el => (this._el = el)}
					style={{ width, height }}
					className="bi-common-word-cloud"
					aria-label={stringResource.get('wordCloudContainer')}
				/>
				<div
					tabIndex={0}
					ref={el => (this._tailEl = el)}
					style={{ width: 0, height: 0, outline: 'none' }}
				/>
			</>
		);
	}
}
